<template>
  <li>
    <div
      class="node-content"
      :class="{ 'selected': isSelected, 'highlight': isHighlighted }"
      @click="selectNode"
    >
      <div class="node-line-container">
        <span
          v-if="!node.is_leaf"
          class="expand-icon"
          :class="{ 'expanded': isOpen }"
          @click.stop="toggle"
        >▶</span>
        <span v-else class="empty-icon"></span>
        <span class="folder-icon">{{ !node.is_leaf ? '📁' : '📄' }}</span>
        <span class="node-label" v-html="highlightedLabel"></span>
      </div>
    </div>
    <transition name="expand">
      <ul v-show="isOpen" v-if="node.children && node.children.length > 0">
        <TreeNode
          v-for="child in node.children"
          :key="child.id"
          :node="child"
          :search-keyword="searchKeyword"
          @node-selected="onChildSelected"
          @has-search-result="handleChildSearchResult"
          ref="childNodes"
        />
      </ul>
    </transition>
  </li>
</template>

<script>
export default {
  name: 'TreeNode',
  props: {
    node: {
      type: Object,
      required: true,
    },
    searchKeyword: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      isOpen: false,
      isSelected: false,
      hasMatchingChild: false
    };
  },
  computed: {
    isHighlighted() {
      if (!this.searchKeyword) return false;
      return this.node.label.toLowerCase().includes(this.searchKeyword.toLowerCase());
    },
    highlightedLabel() {
      if (!this.searchKeyword) return this.node.label;

      const regex = new RegExp(`(${this.escapeRegExp(this.searchKeyword)})`, 'gi');
      return this.node.label.replace(regex, '<span class="highlight">$1</span>');
    },
    hasSearchResult() {
      return this.isHighlighted || this.hasMatchingChild;
    }
  },
  watch: {
    searchKeyword: {
      immediate: true,
      handler(newVal) {
        if (newVal) {
          // 검색어가 있을 때는 검색 결과 확인 및 트리 상태 업데이트
          this.$nextTick(() => {
            this.checkAndExpandForSearch();
          });
        } else {
          // 검색어가 없을 때는 상태 초기화
          this.hasMatchingChild = false;
          this.isOpen = false;
        }
      }
    }
  },
  methods: {
    toggle(event) {
      if (!this.node.is_leaf) {
        this.isOpen = !this.isOpen;
      }
    },
    selectNode() {
      this.$root.$emit('node-selected', this.node.id);
      this.$emit('node-selected', {
        id: this.node.id,
        code: this.node.code,
        label: this.node.label,
        full_label: this.node.full_label
      });
    },
    onChildSelected(nodeData) {
      this.$emit('node-selected', nodeData);
    },
    escapeRegExp(string) {
      return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    },
    handleChildSearchResult(hasResult) {
      this.hasMatchingChild = hasResult;
      this.$emit('has-search-result', this.hasSearchResult);

      if (hasResult && this.searchKeyword) {
        this.isOpen = true;
        this.expandParents();
      }
    },
    async checkAndExpandForSearch() {
      if (!this.node.children || this.node.children.length === 0) {
        // 리프 노드인 경우
        this.$emit('has-search-result', this.isHighlighted);
        return;
      }

      // 자식 노드들의 검색 결과를 재귀적으로 확인
      await this.$nextTick();
      if (this.$refs.childNodes) {
        const childResults = await Promise.all(
          this.$refs.childNodes.map(child => {
            return new Promise(resolve => {
              child.checkAndExpandForSearch().then(() => {
                resolve(child.hasSearchResult);
              });
            });
          })
        );

        this.hasMatchingChild = childResults.some(result => result);
      }

      // 현재 노드나 자식 노드에 검색 결과가 있으면 열기
      if (this.hasSearchResult) {
        this.isOpen = true;
        if (this.$parent && this.$parent.$options.name === 'TreeNode') {
          this.$parent.isOpen = true;
        }
      } else {
        this.isOpen = false;
      }

      this.$emit('has-search-result', this.hasSearchResult);
      return this.hasSearchResult;
    },
    expandParents() {
      let parent = this.$parent;
      while (parent) {
        if (parent.$options.name === 'TreeNode') {
          parent.isOpen = true;
        }
        parent = parent.$parent;
      }
    }
  },
  created() {
    this.$root.$on('node-selected', (selectedId) => {
      this.isSelected = this.node.id === selectedId;
    });

    // expand-to-code 이벤트 리스너 추가
    this.$root.$on('expand-to-code', (code) => {
      if (this.node.code === code) {
        this.isOpen = true;
        // 부모 노드들도 함께 열어줌
        this.expandParents();
      }
    });

    this.$root.$on('reset-tree', () => {
      this.isOpen = false;
      this.isSelected = false;
    });
  },
  beforeDestroy() {
    this.$root.$off('node-selected');
    this.$root.$off('expand-to-code');
    this.$root.$off('reset-tree');
  }
};
</script>


<style scoped>
li {
  list-style: none;
}

.node-content {
  display: flex;
  align-items: center;
  padding: 4px 8px;
  margin: 2px 0;
  border-radius: 4px;
  cursor: pointer;
  user-select: none;
}

.node-content:hover {
  background-color: #f5f5f5;
}

.node-content.selected {
  background-color: #e6f0ff;
}

.node-line-container {
  display: flex;
  align-items: center;
  gap: 4px;
}

.expand-icon {
  width: 16px;
  height: 16px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: transform 0.3s ease;
}

.expand-icon.expanded {
  transform: rotate(90deg);
}

.empty-icon {
  width: 16px;
}

.folder-icon {
  font-size: 14px;
}

.node-label {
  margin-left: 4px;
}

.highlight {
  background-color: #fff176;
  border-radius: 2px;
}

/* 트랜지션 애니메이션 */
.expand-enter-active,
.expand-leave-active {
  transition: all 0.3s ease;
  overflow: hidden;
}

.expand-enter,
.expand-leave-to {
  height: 0;
  opacity: 0;
}

ul {
  padding-left: 20px;
  margin: 0;
}
</style>
